
import React, { useEffect, useState } from "react";
import "./index.scss";
import { Box, Grid, makeStyles, Paper, Typography } from "@material-ui/core";
import AppColor from "utilities/AppColor";
import { useTranslation } from "react-i18next";
import AppButton from "components/CustomButtons/AppButton";
import { useDispatch, useSelector } from "react-redux";
import { getAuthInfo } from "store/slices/auth";
import { SYSTEM_ROLE } from "config/AppConfig";
import { useHistory } from "react-router-dom";

import { fetchWorkerDetail } from "store/slices/worker";

import WarningIcon from "assets/icons/warning.svg";
import DownloadApp from "assets/icons/download-app.svg";
import AppleStore from "assets/icons/app-store.svg";
import PlayStore from "assets/icons/play-store.svg";
import { fetchCompanyDetail } from "store/slices/company";

const styles = {
  container: {
    padding: "1rem",
  },
  title: {
    display: 'flex',
    marginBottom: '1rem',
    '& h5': {
      color: AppColor.mainColor,
    }
  },
  paper: {
    minHeight: 380,
    minWidth: 300,
    padding: '1rem',
    color: AppColor.white,
    display: 'flex',
    flex: '1 0 0px',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    backgroundImage: `url(${require("assets/img/shape.svg")})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundOrigin: 'content-box',
    '& span': {
      zIndex: 1,
      color: AppColor.white
    }
  },
  iconShape: {
    position: 'absolute',
    width: '90%',
    height: '90%'
  },
  warningBox: {
    border: `1px solid ${AppColor.red}`,
    textAlign: 'center',
    padding: '2rem'
  },
  boxDownload: {
    background: 'rgba(113, 80, 134, 0.5)',
    color: AppColor.white,
    padding: '2rem',
    width: '100%'
  },
  boxStore: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  }
};
const useStyles = makeStyles(styles);
const HomePage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const authInfo = useSelector(getAuthInfo) || {};
  const [welcomeText, setWelcomeText] = useState('');
  const [userInfo, setUserInfo] = useState({});

  const {
    clientId,
    id: workerId,
    role,
    companyId
  } = authInfo;

  const {
    approvalStatus
  } = userInfo;

  useEffect(() => {
    if (authInfo) {
      switch (role) {
        case SYSTEM_ROLE.COMPANY: 
          loadCompanyDetail();
          break;
        
        // case SYSTEM_ROLE.SUPER_ADMIN: 
        // case SYSTEM_ROLE.MANAGER:
        //   setIsClient(false);
        //   break;
        case SYSTEM_ROLE.NURSE:
        case SYSTEM_ROLE.SUPPORT_WORKER:
        case SYSTEM_ROLE.SENIOR_SUPPORT_WORKER:
        case SYSTEM_ROLE.TEAM_LEADER:
        case SYSTEM_ROLE.HOUSE_COORDINATOR:
        case SYSTEM_ROLE.REGISTERED_NURSE:
        case SYSTEM_ROLE.CLINICAL_NURSE_MANAGER:
        case SYSTEM_ROLE.OPERATIONS_MANAGER:
        case SYSTEM_ROLE.HR_MANAGER:
        case SYSTEM_ROLE.DIRECTOR:
          loadWorkerDetail();
          break;
        default:
      }
      
      
      
      
      if (authInfo.approvalStatus !== 'ACTIVE') {
        history.push('/profile');
      }
    }
  }, [authInfo]);

  useEffect(() => {
    if (userInfo) {

      if (approvalStatus === 'PENDING' || approvalStatus === 'REJECT') {
        if (role === SYSTEM_ROLE.NURSE || role === SYSTEM_ROLE.SUPPORT_WORKER) {
          setWelcomeText('text.welcomeTextWorkerPending');
        } else if (role === SYSTEM_ROLE.COMPANY) {
          setWelcomeText('text.welcomeTextCompanyPending');
        } else {
          setWelcomeText('text.welcomeTextClientPending');
        }
      } else {
        setWelcomeText('text.welcomeTextActive');
      }
    }
  }, [userInfo]);

  const loadWorkerDetail = async () => {
    const result = await dispatch(fetchWorkerDetail({ workerId: workerId }));
    if (result.payload) {
      setUserInfo({ ...result.payload });
    }
  };

  const loadCompanyDetail = async () => {
    const result = await dispatch(fetchCompanyDetail({ companyId: companyId }));
    if (result.payload) {
      setUserInfo({ ...result.payload });
    }
  }

  return (
    <Box display="flex" flexWrap="wrap" alignItems="center">
      <Box className={classes.paper}>
        <Box padding="3rem 5rem 4rem 4rem">
          <Box fontSize="2rem" fontWeight={700}>{t('label.welcome')}</Box>
          {
            approvalStatus === 'PENDING' || approvalStatus === 'REJECT'
              ? (
                <Box>{t(welcomeText)}</Box>
              )
              : (
                <React.Fragment>
                  <Box>{t(welcomeText)}</Box>
                </React.Fragment>
              )
          }
        </Box>
      </Box>
      <Box flex="1 0 0px" minWidth={300}>
        {
          approvalStatus === 'PENDING' || approvalStatus === 'REJECT'
            ? (
              <Paper className="p-3">
                <Box className={classes.title}>
                  <Typography variant="h5">
                    {t("label.completeYourProfile")}
                  </Typography>
                </Box>
                <Box>{t('text.completeYourProfile')}</Box>
                <Box>
                  <AppButton
                    label={`${t("label.completeNow")}`}
                    rounder
                    onClick={() => history.push('/profile')}
                  />
                </Box>
              </Paper>
            )
            : (
              <React.Fragment>
                <Paper className={classes.warningBox}>
                  <Box>
                    <img src={WarningIcon} />
                  </Box>
                  <Box fontWeight={700}>{t('text.pleaseIntallTheMobileApp')}</Box>
                  <Box>{t('text.appFeaturesAreOnlySupported')}</Box>
                </Paper>
              </React.Fragment>
            )
        }
      </Box>
      {
        approvalStatus !== 'PENDING' && approvalStatus !== 'REJECT' && (
          <Box className={classes.boxDownload}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={6}>
                <Box className={classes.boxStore}>
                  <Box className="mb-3" fontSize="2rem">
                    {t('text.downloadOurFreeApp')}
                  </Box>
                  <Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={6}>
                        <img src={PlayStore} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <img src={AppleStore} />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Box textAlign="center">
                  <img src={DownloadApp} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        )
      }
    </Box >
  );
};

export default HomePage;
